import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSubscription, useUpdateSubscription } from '../../hooks/useSubscriptions';
import { getAuditLogs, createCalendarEvent, CalendarEventCategory, updatePax8SubscriptionQuantity } from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, CheckCircle, HelpCircle, ChevronDown, ChevronUp, CalendarDays, PencilLine } from 'lucide-react';
import toast from 'react-hot-toast';
import { AuditLog } from '../../types';

export default function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { subscription, loading, error, refetch } = useSubscription(id);
  const { updateSubscription, loading: updating } = useUpdateSubscription();

  const [showCancellationForm, setShowCancellationForm] = useState(false);
  const [cancellationDate, setCancellationDate] = useState('');
  const [showModificationLog, setShowModificationLog] = useState(false);
  const [showAutoRenewHelp, setShowAutoRenewHelp] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Add to Calendar modal state
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarEventTitle, setCalendarEventTitle] = useState('');
  const [calendarEventDescription, setCalendarEventDescription] = useState('');
  const [calendarEventDate, setCalendarEventDate] = useState('');
  const [calendarEventCategory, setCalendarEventCategory] = useState<CalendarEventCategory>('reminder');
  const [savingCalendarEvent, setSavingCalendarEvent] = useState(false);

  // Change Quantity modal state
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [newQuantity, setNewQuantity] = useState(1);
  const [updatingQuantity, setUpdatingQuantity] = useState(false);

  // Fetch audit logs when subscription is loaded or when showing log
  useEffect(() => {
    if (id && showModificationLog) {
      setLogsLoading(true);
      getAuditLogs({ entityType: 'subscription', entityId: id, pageSize: 50 })
        .then((result) => setAuditLogs(result.items))
        .catch((err) => console.error('Failed to load audit logs:', err))
        .finally(() => setLogsLoading(false));
    }
  }, [id, showModificationLog]);

  // Helper to format change values for display
  const formatChangeValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'On' : 'Off';
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      try {
        return format(parseISO(value), 'MMM d, yyyy h:mm a');
      } catch {
        return value;
      }
    }
    return String(value);
  };

  // Helper to get friendly field names
  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      autoRenew: 'Recurring Billing',
      status: 'Status',
      quantity: 'Quantity',
      futureCancellationSubmitted: 'Cancellation Submitted',
      futureCancellationDate: 'Cancellation Date',
      futureCancellationVerified: 'Cancellation Verified',
      futureCancellationVerifiedDate: 'Verification Date',
      notes: 'Notes',
    };
    return labels[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">
          Subscription not found
        </h3>
        <p className="text-muted-foreground mt-2">{error}</p>
        <Link to="/subscriptions" className="btn-primary mt-4 inline-flex">
          Back to Subscriptions
        </Link>
      </div>
    );
  }

  const handleMarkCancellation = async () => {
    try {
      await updateSubscription({
        id: subscription.id,
        data: {
          futureCancellationSubmitted: true,
          futureCancellationDate: cancellationDate || null,
        },
      });
      toast.success('Cancellation marked successfully');
      setShowCancellationForm(false);
      refetch();
    } catch {
      toast.error('Failed to mark cancellation');
    }
  };

  const handleVerifyCancellation = async () => {
    try {
      await updateSubscription({
        id: subscription.id,
        data: {
          futureCancellationVerified: true,
          futureCancellationVerifiedDate: new Date().toISOString(),
        },
      });
      toast.success('Cancellation verified');
      refetch();
    } catch {
      toast.error('Failed to verify cancellation');
    }
  };

  const openAddToCalendarModal = () => {
    // Pre-populate with subscription info
    const renewalDate = subscription.msNextLifecycleDate || subscription.renewalDate;
    setCalendarEventTitle(`Renewal: ${subscription.productName}`);
    setCalendarEventDescription(`Subscription renewal for ${subscription.clientName}\nQuantity: ${subscription.quantity} licenses\nPrice: $${subscription.totalPrice.toFixed(2)}\nAuto-renew: ${subscription.autoRenew === null ? 'Unknown' : subscription.autoRenew ? 'Yes' : 'No'}`);
    setCalendarEventDate(renewalDate.split('T')[0]);
    setCalendarEventCategory('reminder');
    setShowCalendarModal(true);
  };

  const handleSaveCalendarEvent = async () => {
    if (!calendarEventTitle || !calendarEventDate) {
      toast.error('Title and date are required');
      return;
    }

    setSavingCalendarEvent(true);
    try {
      await createCalendarEvent({
        title: calendarEventTitle,
        description: calendarEventDescription,
        date: calendarEventDate,
        category: calendarEventCategory,
        clientId: subscription.clientId,
        clientName: subscription.clientName,
        subscriptionId: subscription.id,
        subscriptionName: subscription.productName,
      });
      toast.success('Event added to calendar');
      setShowCalendarModal(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create calendar event');
    } finally {
      setSavingCalendarEvent(false);
    }
  };

  const openQuantityModal = () => {
    setNewQuantity(subscription.quantity);
    setShowQuantityModal(true);
  };

  const handleUpdateQuantity = async () => {
    if (newQuantity === subscription.quantity) {
      toast.error('Quantity is unchanged');
      return;
    }
    if (newQuantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    setUpdatingQuantity(true);
    try {
      const result = await updatePax8SubscriptionQuantity(subscription.id, newQuantity);
      if (result.success) {
        toast.success(result.message || `Quantity updated to ${newQuantity}`);
        setShowQuantityModal(false);
        refetch();
      } else {
        toast.error(result.error || 'Failed to update quantity');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update quantity');
    } finally {
      setUpdatingQuantity(false);
    }
  };

  const utilizationPercent =
    subscription.assignedQuantity !== null && subscription.quantity > 0
      ? Math.round((subscription.assignedQuantity / subscription.quantity) * 100)
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate('/subscriptions')}
            className="p-2 rounded-md hover:bg-accent mt-1"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {subscription.productName}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {subscription.clientName}
            </p>
            <div className="flex items-center space-x-2 mt-2">
              <StatusBadge status={subscription.source} type="source" />
              <StatusBadge
                status={subscription.autoRenew ? 'auto-renew-on' : 'auto-renew-off'}
                type="autoRenew"
              />
              <StatusBadge status={subscription.status} type="subscription" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Subscription Details */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">
              Subscription Details
            </h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-muted-foreground flex items-center">
                  SKU
                  <span className="ml-1 text-muted-foreground cursor-help" title="Stock Keeping Unit - the Microsoft product identifier code">
                    <HelpCircle className="h-4 w-4" />
                  </span>
                </dt>
                <dd className="mt-1 text-sm font-mono text-foreground">
                  {subscription.sku || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center">
                  Quantity
                  <span className="ml-1 text-muted-foreground cursor-help" title="Number of licenses purchased for this subscription">
                    <HelpCircle className="h-4 w-4" />
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-foreground flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {subscription.quantity} {subscription.quantity === 1 ? 'license' : 'licenses'}
                  </span>
                  {subscription.source === 'pax8' && (
                    <button
                      onClick={openQuantityModal}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      title="Change license quantity"
                    >
                      <PencilLine className="h-3.5 w-3.5" />
                      Change
                    </button>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center">
                  Billing Term
                  <span className="ml-1 text-muted-foreground cursor-help" title="How often the subscription is BILLED/PAID (monthly or annual). This is different from commitment term.">
                    <HelpCircle className="h-4 w-4" />
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-foreground capitalize">
                  {subscription.billingTerm}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground flex items-center">
                  Commitment Term
                  <span className="ml-1 text-muted-foreground cursor-help" title="How long you're COMMITTED to the subscription. Annual (1-Year) commitment = lower price but locked in for a year. Monthly commitment = higher price but can cancel anytime. NOTE: This is separate from billing frequency!">
                    <HelpCircle className="h-4 w-4" />
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {subscription.commitmentTerm || (
                    subscription.source === 'direct' && subscription.msNextLifecycleDate ? (
                      // For MS Direct, if we have expiration date, it's likely annual commitment
                      <span>
                        1-Year
                        <span className="ml-2 text-xs text-muted-foreground">(inferred from MS expiration)</span>
                      </span>
                    ) : subscription.source === 'direct' ? (
                      <span className="text-yellow-600 dark:text-yellow-400">Unknown - check MS Admin</span>
                    ) : (
                      // Fallback for Pax8 without commitment term
                      '1-Year'
                    )
                  )}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-sm text-muted-foreground flex items-center">
                  Recurring Billing Status
                  <button
                    onClick={() => setShowAutoRenewHelp(!showAutoRenewHelp)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                    title="Click for help"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </dt>
                {showAutoRenewHelp && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-medium mb-2">How to check Recurring Billing status:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Log into <strong>Microsoft Admin Center</strong> as the customer</li>
                      <li>Go to <strong>Billing → Your products</strong></li>
                      <li>Find this subscription and check <strong>"Recurring billing"</strong> (On/Off)</li>
                      <li>Click the matching button below to record the status</li>
                    </ol>
                    <p className="mt-2 text-xs text-blue-600 dark:text-blue-300">
                      This helps track which subscriptions will auto-renew vs expire.
                    </p>
                  </div>
                )}
                <dd className="mt-2 flex items-center space-x-3">
                  <StatusBadge
                    status={subscription.autoRenew === true ? 'auto-renew-on' : subscription.autoRenew === false ? 'auto-renew-off' : 'unknown'}
                    type="autoRenew"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={async () => {
                        try {
                          await updateSubscription({ id: subscription.id, data: { autoRenew: true } });
                          toast.success('Marked as Recurring: On');
                          refetch();
                        } catch { toast.error('Failed to update'); }
                      }}
                      disabled={updating || subscription.autoRenew === true}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border ${subscription.autoRenew === true ? 'bg-green-100 text-green-700 border-green-300 cursor-not-allowed' : 'bg-card hover:bg-green-50 dark:hover:bg-green-900/30 text-foreground hover:text-green-700 dark:hover:text-green-300 border-border'}`}
                      title="Microsoft shows 'Recurring billing: On'"
                    >
                      Set to On
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await updateSubscription({ id: subscription.id, data: { autoRenew: false } });
                          toast.success('Marked as Recurring: Off');
                          refetch();
                        } catch { toast.error('Failed to update'); }
                      }}
                      disabled={updating || subscription.autoRenew === false}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border ${subscription.autoRenew === false ? 'bg-orange-100 text-orange-700 border-orange-300 cursor-not-allowed' : 'bg-card hover:bg-orange-50 dark:hover:bg-orange-900/30 text-foreground hover:text-orange-700 dark:hover:text-orange-300 border-border'}`}
                      title="Microsoft shows 'Recurring billing: Off'"
                    >
                      Set to Off
                    </button>
                  </div>
                </dd>
              </div>
              {/* Only show Pax8 Commitment End for Pax8-sourced subscriptions */}
              {subscription.source === 'pax8' && (
                <div>
                  <dt className="text-sm text-muted-foreground flex items-center">
                    Pax8 Commitment End
                    <span className="ml-1 text-muted-foreground cursor-help" title="When your pricing agreement with Pax8 ends. After this date, Pax8 may adjust your pricing. This is separate from the MS renewal date.">
                      <HelpCircle className="h-4 w-4" />
                    </span>
                  </dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {format(parseISO(subscription.commitmentEndDate), 'MMMM d, yyyy')}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-muted-foreground flex items-center">
                  {subscription.source === 'direct' ? 'MS Renewal/Expiration' : (subscription.msNextLifecycleDate ? 'MS Expiration Date' : 'Renewal Date')}
                  <span className="ml-1 text-muted-foreground cursor-help" title={subscription.msNextLifecycleDate ? "The actual Microsoft renewal/expiration date based on the original purchase anniversary. If Recurring Billing is ON, subscription auto-renews on this date. If OFF, it expires and stops working." : "Estimated renewal date. Run 'Verify Billing' on the client page to get the actual MS date."}>
                    <HelpCircle className="h-4 w-4" />
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {subscription.msNextLifecycleDate
                    ? format(parseISO(subscription.msNextLifecycleDate), 'MMMM d, yyyy')
                    : format(parseISO(subscription.renewalDate), 'MMMM d, yyyy')}
                  {subscription.msNextLifecycleDate ? (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400">(verified from Microsoft)</span>
                  ) : (
                    <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">(estimate - verify with MS)</span>
                  )}
                </dd>
              </div>
              {subscription.unitPrice > 0 && (
                <>
                  <div>
                    <dt className="text-sm text-muted-foreground">Unit Price</dt>
                    <dd className="mt-1 text-sm text-foreground">
                      ${subscription.unitPrice.toFixed(2)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Total Price</dt>
                    <dd className="mt-1 text-sm text-foreground">
                      ${subscription.totalPrice.toFixed(2)}
                    </dd>
                  </div>
                </>
              )}
              {subscription.pax8SubscriptionId && (
                <div className="col-span-2">
                  <dt className="text-sm text-muted-foreground">Pax8 Subscription ID</dt>
                  <dd className="mt-1 text-sm font-mono text-foreground">
                    {subscription.pax8SubscriptionId}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Cancellation Management */}
          <div className="card p-6">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Cancellation Management
              </h3>
              <span className="ml-2 text-muted-foreground cursor-help" title={`Track subscription cancellations you've submitted to ${subscription.source === 'pax8' ? 'Pax8' : 'Microsoft'}. Use this to mark when you've requested a cancellation and verify it was processed. This is for YOUR tracking - it doesn't actually cancel the subscription.`}>
                <HelpCircle className="h-5 w-5" />
              </span>
            </div>

            <div className="mb-4 p-3 bg-muted rounded-lg text-sm text-muted-foreground">
              <p><strong>What is this?</strong> Use this section to track cancellations you've submitted through {subscription.source === 'pax8' ? 'Pax8' : 'Microsoft Admin Center'}.</p>
              <ul className="mt-2 list-disc list-inside text-xs space-y-1">
                <li><strong>Mark Cancellation Submitted</strong> - Record that you've requested this subscription be cancelled</li>
                <li><strong>Mark as Verified</strong> - Confirm the cancellation was processed after checking {subscription.source === 'pax8' ? 'with Pax8' : 'in Microsoft Admin Center'}</li>
              </ul>
              <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">Note: This is for tracking only - it does NOT cancel the subscription automatically.</p>
            </div>

            {subscription.futureCancellationSubmitted ? (
              <div className="space-y-4">
                <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 dark:text-orange-200">
                    Cancellation Submitted
                  </h4>
                  {subscription.futureCancellationDate && (
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      Scheduled for: {format(parseISO(subscription.futureCancellationDate), 'MMMM d, yyyy')}
                    </p>
                  )}

                  {subscription.futureCancellationVerified ? (
                    <div className="mt-3 flex items-center text-green-600 dark:text-green-400">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Verified on {format(parseISO(subscription.futureCancellationVerifiedDate!), 'MMMM d, yyyy')}
                    </div>
                  ) : (
                    <button
                      onClick={handleVerifyCancellation}
                      disabled={updating}
                      className="mt-3 btn-primary btn-sm"
                    >
                      {updating ? 'Verifying...' : 'Mark as Verified'}
                    </button>
                  )}
                </div>
              </div>
            ) : showCancellationForm ? (
              <div className="space-y-4">
                <div>
                  <label className="label">Cancellation Date (optional)</label>
                  <input
                    type="date"
                    value={cancellationDate}
                    onChange={(e) => setCancellationDate(e.target.value)}
                    className="input"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleMarkCancellation}
                    disabled={updating}
                    className="btn-primary"
                  >
                    {updating ? 'Saving...' : 'Submit Cancellation'}
                  </button>
                  <button
                    onClick={() => setShowCancellationForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCancellationForm(true)}
                className="btn-danger"
              >
                Mark Cancellation Submitted
              </button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Utilization */}
          {subscription.assignedQuantity !== null && (
            <div className="card p-6">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  License Utilization
                </h3>
                <span className="ml-2 text-muted-foreground cursor-help" title="Shows how many licenses are actually assigned to users vs how many are purchased. Low utilization means you may be paying for unused licenses.">
                  <HelpCircle className="h-5 w-5" />
                </span>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-foreground">
                  {utilizationPercent}%
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {subscription.assignedQuantity} of {subscription.quantity} assigned
                </p>
                <div className="w-full bg-muted rounded-full h-3 mt-4">
                  <div
                    className={`h-3 rounded-full ${
                      utilizationPercent! < 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${utilizationPercent}%` }}
                  />
                </div>
                {utilizationPercent! < 50 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                    Low utilization - consider reducing licenses
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Quick Info */}
          <div className="card p-6">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Quick Info
              </h3>
              <span className="ml-2 text-muted-foreground cursor-help" title="Key dates for this subscription: when it was created in our system, last modified, and last synced from Pax8/Microsoft.">
                <HelpCircle className="h-5 w-5" />
              </span>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="text-foreground">
                  {format(parseISO(subscription.createdDate), 'MMM d, yyyy')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Last Modified</dt>
                <dd className="text-foreground">
                  {format(parseISO(subscription.modifiedDate), 'MMM d, yyyy')}
                </dd>
              </div>
              {subscription.lastSyncDate && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Last Synced</dt>
                  <dd className="text-foreground">
                    {format(parseISO(subscription.lastSyncDate), 'MMM d, yyyy')}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Actions */}
          <div className="card p-6 space-y-3">
            {subscription.source === 'pax8' && (
              <button
                onClick={openQuantityModal}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PencilLine className="h-5 w-5" />
                Change Quantity
              </button>
            )}
            <button
              onClick={openAddToCalendarModal}
              className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <CalendarDays className="h-5 w-5" />
              Add to Calendar
            </button>
            <Link
              to={`/clients/${subscription.clientId}`}
              className="block text-center btn-secondary w-full"
            >
              View Client
            </Link>
          </div>
        </div>
      </div>

      {/* Modification History */}
      <div className="card">
        <button
          onClick={() => setShowModificationLog(!showModificationLog)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-accent rounded-lg"
        >
          <h3 className="text-lg font-medium text-foreground">
            Modification History
          </h3>
          {showModificationLog ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        {showModificationLog && (
          <div className="px-4 pb-4">
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No modifications recorded yet.
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="border border-border rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-sm font-medium text-foreground">
                          {log.userEmail}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {format(parseISO(log.timestamp), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {log.action}
                      </span>
                    </div>
                    {Object.keys(log.changes).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(log.changes).map(([field, change]) => (
                          <div key={field} className="text-xs">
                            <span className="font-medium text-foreground">
                              {getFieldLabel(field)}:
                            </span>
                            <span className="text-red-600 dark:text-red-400 ml-1 line-through">
                              {formatChangeValue(change.old)}
                            </span>
                            <span className="text-muted-foreground mx-1">→</span>
                            <span className="text-green-600 dark:text-green-400">
                              {formatChangeValue(change.new)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add to Calendar Modal */}
      {showCalendarModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCalendarModal(false)}
        >
          <div
            className="bg-card rounded-xl border border-border w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">
                Add to Renewal Calendar
              </h3>
              <button
                onClick={() => setShowCalendarModal(false)}
                className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={calendarEventTitle}
                  onChange={(e) => setCalendarEventTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={calendarEventDate}
                  onChange={(e) => setCalendarEventDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Category
                </label>
                <select
                  value={calendarEventCategory}
                  onChange={(e) => setCalendarEventCategory(e.target.value as CalendarEventCategory)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="reminder">Reminder</option>
                  <option value="action-required">Action Required</option>
                  <option value="follow-up">Follow-up</option>
                  <option value="migration">Migration</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Description
                </label>
                <textarea
                  value={calendarEventDescription}
                  onChange={(e) => setCalendarEventDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Info */}
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                <p>This event will be visible to all users on the Renewal Calendar.</p>
                <p className="mt-1">Linked to: <strong>{subscription.clientName}</strong> - {subscription.productName}</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className="flex-1 py-2.5 rounded-lg bg-muted hover:bg-accent text-foreground font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCalendarEvent}
                  disabled={savingCalendarEvent || !calendarEventTitle || !calendarEventDate}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {savingCalendarEvent && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {savingCalendarEvent ? 'Saving...' : 'Add to Calendar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Quantity Modal */}
      {showQuantityModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowQuantityModal(false)}
        >
          <div
            className="bg-card rounded-xl border border-border w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">
                Change License Quantity
              </h3>
              <button
                onClick={() => setShowQuantityModal(false)}
                className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Product Info */}
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{subscription.productName}</p>
                <p>{subscription.clientName}</p>
              </div>

              {/* Current Quantity */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Current quantity:</span>
                <span className="text-sm font-semibold text-foreground">
                  {subscription.quantity} {subscription.quantity === 1 ? 'license' : 'licenses'}
                </span>
              </div>

              {/* New Quantity Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  New Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Change Summary */}
              {newQuantity !== subscription.quantity && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700 dark:text-blue-300">Change:</span>
                    <span className={`font-semibold ${newQuantity > subscription.quantity ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {newQuantity > subscription.quantity ? '+' : ''}{newQuantity - subscription.quantity} {Math.abs(newQuantity - subscription.quantity) === 1 ? 'license' : 'licenses'}
                    </span>
                  </div>
                  {subscription.unitPrice > 0 && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-blue-700 dark:text-blue-300">New monthly total:</span>
                      <span className="font-semibold text-blue-800 dark:text-blue-200">
                        ${(newQuantity * subscription.unitPrice).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Billing Warning */}
              <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium">Billing Impact</p>
                    <p className="mt-1 text-xs">
                      {newQuantity > subscription.quantity
                        ? 'Increasing quantity will update your Pax8 billing. Additional licenses take effect immediately.'
                        : newQuantity < subscription.quantity
                        ? 'Decreasing quantity will update your Pax8 billing. Removed licenses may be pro-rated.'
                        : 'Changes to quantity will affect your Pax8 billing.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowQuantityModal(false)}
                  className="flex-1 py-2.5 rounded-lg bg-muted hover:bg-accent text-foreground font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateQuantity}
                  disabled={updatingQuantity || newQuantity === subscription.quantity || newQuantity < 1}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {updatingQuantity && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {updatingQuantity ? 'Updating...' : 'Update Quantity'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
